/**
 * Conexión de la cuenta de Gmail desde la que salen los recordatorios de
 * mantenimiento. Dentro de /settings y solo para admin.
 */
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Mail } from 'lucide-react'
import {
  ApiError,
  deleteEmailSettings,
  disconnectGmail,
  getEmailSettings,
  saveEmailSettings,
  testEmailSettings,
} from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { Field } from '@/components/ui/field'

export function EmailSettingsPanel() {
  const qc = useQueryClient()
  const query = useQuery({ queryKey: ['email-settings'], queryFn: getEmailSettings })

  const [user, setUser] = useState('')
  const [fromName, setFromName] = useState('GKD Fleet')
  const [password, setPassword] = useState('')
  const [recipients, setRecipients] = useState('')
  const [enabled, setEnabled] = useState(true)
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null)

  const settings = query.data?.settings ?? null
  const gmail = query.data?.gmail ?? null

  // El resultado de la vuelta desde Google llega en la URL (/settings?gmail=…):
  // el callback no puede devolver JSON, porque quien navega es el navegador.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const estado = params.get('gmail')
    if (!estado) return
    setMessage(
      estado === 'connected'
        ? { kind: 'ok', text: `Cuenta conectada: ${params.get('account') ?? ''}` }
        : { kind: 'error', text: params.get('msg') ?? 'No se pudo conectar la cuenta.' },
    )
    // Se limpia la URL para que un refresco no vuelva a mostrar el mismo aviso.
    window.history.replaceState({}, '', window.location.pathname)
    void qc.invalidateQueries({ queryKey: ['email-settings'] })
  }, [qc])

  // Los campos se rellenan cuando llega la configuración guardada. La
  // contraseña nunca viaja de vuelta, así que su campo se queda vacío y solo se
  // manda si se escribe una nueva.
  useEffect(() => {
    if (!settings) return
    setUser(settings.user)
    setFromName(settings.fromName)
    setRecipients(settings.recipients.join(', '))
    setEnabled(settings.enabled)
  }, [settings])

  const save = useMutation({
    mutationFn: () =>
      saveEmailSettings({
        enabled,
        user,
        fromName,
        password,
        recipients: recipients
          .split(/[,\n;]/)
          .map((r) => r.trim())
          .filter(Boolean),
      }),
    onSuccess: async () => {
      setPassword('')
      setMessage({ kind: 'ok', text: 'Cuenta guardada.' })
      await qc.invalidateQueries({ queryKey: ['email-settings'] })
    },
    onError: (err) =>
      setMessage({
        kind: 'error',
        text: err instanceof ApiError ? err.message : 'No se pudo guardar.',
      }),
  })

  const test = useMutation({
    mutationFn: (send: boolean) => testEmailSettings(send),
    onSuccess: (res, send) =>
      setMessage(
        res.ok
          ? {
              kind: 'ok',
              text: send
                ? `Correo de prueba enviado a: ${(res.recipients ?? []).join(', ')}`
                : 'Gmail acepta las credenciales.',
            }
          : { kind: 'error', text: res.error ?? 'Falló la prueba.' },
      ),
    onError: (err) =>
      setMessage({
        kind: 'error',
        text: err instanceof ApiError ? err.message : 'No se pudo probar.',
      }),
  })

  const unlinkGmail = useMutation({
    mutationFn: disconnectGmail,
    onSuccess: async () => {
      setMessage({ kind: 'ok', text: 'Cuenta de Gmail desconectada.' })
      await qc.invalidateQueries({ queryKey: ['email-settings'] })
    },
  })

  const disconnect = useMutation({
    mutationFn: deleteEmailSettings,
    onSuccess: async () => {
      setUser('')
      setPassword('')
      setRecipients('')
      setMessage({ kind: 'ok', text: 'Cuenta desconectada.' })
      await qc.invalidateQueries({ queryKey: ['email-settings'] })
    },
  })

  return (
    <Card className="lg:col-span-2">
      <CardHeader
        title="Correo de recordatorios"
        action={
          settings?.hasPassword ? (
            <span className="text-xs text-muted-foreground">
              {settings.enabled ? 'Conectada y activa' : 'Conectada, envío desactivado'}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">Sin conectar</span>
          )
        }
      />
      <CardBody className="space-y-3">
        <p className="text-sm text-muted-foreground">
          <Mail className="mr-1.5 inline size-4" aria-hidden />
          Los recordatorios de mantenimiento salen de esta cuenta a las <strong>10:00 (hora
          del Este)</strong>: una semana antes, tres días antes, el día antes y el mismo día. Los
          dos primeros incluyen el evento para añadir al calendario. Si el envío cae en sábado o
          domingo, se manda el lunes siguiente.
        </p>

        <div className="rounded-xl border border-border p-3">
          <p className="text-sm font-medium">Conexión con Google (recomendada)</p>
          {gmail?.connected ? (
            <>
              <p className="mt-1 text-sm text-muted-foreground">
                Conectada como <strong>{gmail.connectedEmail}</strong>
                {gmail.connectedAt
                  ? ` · desde el ${new Date(gmail.connectedAt).toLocaleDateString()}`
                  : ''}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Permiso concedido: solo enviar ({gmail.scope?.includes('gmail.send') ? 'gmail.send' : gmail.scope}).
                No da acceso a leer el buzón.
              </p>
              <div className="mt-2 flex gap-2">
                <a
                  href="/api/gmail/connect"
                  className="inline-flex min-h-11 items-center rounded-lg border border-border bg-card px-3 text-sm font-medium hover:bg-muted"
                >
                  Reconectar
                </a>
                <Button
                  type="button"
                  variant="ghost"
                  loading={unlinkGmail.isPending}
                  onClick={() => unlinkGmail.mutate()}
                >
                  Desconectar
                </Button>
              </div>
            </>
          ) : gmail?.configured ? (
            <>
              <p className="mt-1 text-sm text-muted-foreground">
                Sin conectar. Autoriza la cuenta desde Google: no se guarda ninguna contraseña y el
                permiso concedido es solo el de enviar.
              </p>
              {/* Enlace, no fetch: el navegador tiene que NAVEGAR a Google. */}
              <a
                href="/api/gmail/connect"
                className="mt-2 inline-flex min-h-11 items-center rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                Conectar cuenta de Gmail
              </a>
            </>
          ) : (
            <p className="mt-1 text-sm text-muted-foreground">
              El servidor todavía no tiene las credenciales de la aplicación de Google
              (<code>GMAIL_CLIENT_ID</code>, <code>GMAIL_CLIENT_SECRET</code>,{' '}
              <code>GMAIL_REDIRECT_URI</code>). Mientras tanto puedes usar el camino de abajo, con
              contraseña de aplicación.
            </p>
          )}
        </div>

        {!gmail?.connected && (
          <div className="rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
            <strong>Alternativa sin Google Cloud:</strong> una{' '}
            <strong>contraseña de aplicación</strong> de Google (16 caracteres), no la contraseña
            normal del correo — Google no admite esa por SMTP desde 2022. Se genera en la cuenta de
            Google → Seguridad → Verificación en dos pasos → Contraseñas de aplicaciones.
          </div>
        )}

        {message && (
          <p
            role="alert"
            className={
              message.kind === 'ok'
                ? 'text-sm font-medium text-success-foreground'
                : 'text-sm font-medium text-danger'
            }
          >
            {message.text}
          </p>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          {!gmail?.connected && (
            <Field
              label="Cuenta de Gmail (solo si usas contraseña de aplicación)"
              type="email"
              value={user}
              onChange={(e) => setUser(e.target.value)}
              placeholder="flota@galaxykitchendepot.com"
            />
          )}
          <Field
            label="Nombre del remitente"
            value={fromName}
            onChange={(e) => setFromName(e.target.value)}
          />
        </div>

        {!gmail?.connected && (
          <Field
            label="Contraseña de aplicación"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={settings?.hasPassword ? 'Guardada — escribe una nueva para cambiarla' : ''}
          />
        )}

        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium">Destinatarios</span>
          <textarea
            rows={2}
            value={recipients}
            onChange={(e) => setRecipients(e.target.value)}
            placeholder="Separados por comas. Si lo dejas vacío, se avisa a los admin y manager activos."
            className="rounded-lg border border-input bg-card px-3 py-2 text-sm"
          />
          {query.data?.effectiveRecipients?.length ? (
            <span className="text-xs text-muted-foreground">
              Ahora mismo recibirían: {query.data.effectiveRecipients.join(', ')}
            </span>
          ) : null}
        </label>

        <label className="flex min-h-11 items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="size-4"
          />
          <span>Enviar recordatorios automáticamente</span>
        </label>

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            loading={save.isPending}
            disabled={!gmail?.connected && (!user || (!password && !settings?.hasPassword))}
            onClick={() => {
              setMessage(null)
              save.mutate()
            }}
          >
            Guardar
          </Button>
          <Button
            type="button"
            variant="outline"
            loading={test.isPending && test.variables === false}
            disabled={!gmail?.connected && !settings?.hasPassword}
            onClick={() => {
              setMessage(null)
              test.mutate(false)
            }}
          >
            Probar credenciales
          </Button>
          <Button
            type="button"
            variant="outline"
            loading={test.isPending && test.variables === true}
            disabled={!gmail?.connected && !settings?.hasPassword}
            onClick={() => {
              setMessage(null)
              test.mutate(true)
            }}
          >
            Enviar correo de prueba
          </Button>
          {settings?.hasPassword && (
            <Button
              type="button"
              variant="ghost"
              loading={disconnect.isPending}
              onClick={() => disconnect.mutate()}
            >
              Desconectar
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  )
}
